/**
 * `/messages` — the notice board in full.
 *
 * The home page carries only the `HOME` subset, deliberately short so it does
 * not push the conversation off the screen. This page is where the rest lives,
 * which is what makes demoting a notice to `CENTER` a real option for an admin
 * rather than a euphemism for deleting it.
 *
 * Grouped by kind, in the board's reading order (see `message-kinds.ts`), with
 * no kind filter: four short blocks are faster to read than a filter is to
 * operate, and a filter would let someone hide the alert block.
 */

import { Icon } from "@norican/icons";
import { Typography } from "@norican/react";

import {
  AppBreadcrumbs,
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";

import { useMessages } from "./api";
import { MessageCard } from "./message-card";
import {
  groupMessagesByKind,
  MESSAGE_KIND_ICON,
  MESSAGE_KIND_TONE,
  useMessageKindLabel,
} from "./message-kinds";

export function MessagesPage() {
  const { t } = useT("messages");
  useDocumentTitle(t("documentTitle"));

  const kindLabel = useMessageKindLabel();
  const query = useMessages();
  const groups = groupMessagesByKind(query.data ?? []);

  return (
    <PageContainer
      title={t("title")}
      subtitle={t("subtitle")}
      breadcrumbs={<AppBreadcrumbs items={[{ label: t("title") }]} />}
    >
      {query.isLoading ? (
        <LoadingRows rows={3} />
      ) : query.error != null ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : groups.length === 0 ? (
        <EmptyStateBlock title={t("empty.title")} description={t("empty.hint")} />
      ) : (
        groups.map((group) => {
          const tone = MESSAGE_KIND_TONE[group.kind];
          return (
            <section key={group.kind} className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Icon
                  icon={MESSAGE_KIND_ICON[group.kind]}
                  size="sm"
                  {...(tone === "neutral" ? {} : { tone })}
                />
                <Typography
                  as="h2"
                  variant="label"
                  tone="muted"
                  className="tracking-wide uppercase"
                >
                  {kindLabel(group.kind)}
                </Typography>
              </div>
              {group.items.map((message) => (
                <MessageCard key={message.message_id} message={message} />
              ))}
            </section>
          );
        })
      )}
    </PageContainer>
  );
}
